import { bvNaam } from './staat'

/* ===========================================================================
 *  De velden van Exact, leesbaar
 *
 *  Exact noemt een rekening GLAccountCode en een datum /Date(1696118400000)/.
 *  Hier staat wat dat in het Nederlands is, en hoe een waarde getoond wordt.
 *
 *  Een vertaling is een HULP en geen vervanging: de naam van Exact staat er
 *  altijd bij (in de lade, en als tooltip op elke kolom). Wie een filter in
 *  Live uit Exact wil maken, of met de boekhouder praat, heeft die naam nodig.
 *  Een veld dat hier niet staat, houdt gewoon zijn naam van Exact.
 * =========================================================================== */

/* Wat wij zelf uit elke rij tillen; zie filter.ts in de functie. */
export const EIGEN_VELDEN: Record<string, string> = {
  '@datum': 'Datum',
  '@bedrag': 'Bedrag',
  '@nummer': 'Nummer',
  '@omschrijving': 'Omschrijving',
  '@division': 'Administratie',
  '@exactId': 'Exact-ID',
}

const LABELS: Record<string, string> = {
  ID: 'ID', Timestamp: 'Wijzigingsteller', Division: 'Administratie (nr.)', Created: 'Aangemaakt', Modified: 'Gewijzigd',
  Creator: 'Aangemaakt door (ID)', CreatorFullName: 'Aangemaakt door', Modifier: 'Gewijzigd door (ID)', ModifierFullName: 'Gewijzigd door',
  EntryID: 'Boeking (ID)', EntryNumber: 'Boekstuknummer', EntryDate: 'Boekdatum', LineNumber: 'Regel',
  Date: 'Datum', DueDate: 'Vervaldatum', InvoiceDate: 'Factuurdatum', DocumentDate: 'Documentdatum',
  FinancialYear: 'Boekjaar', FinancialPeriod: 'Periode', ReportingYear: 'Boekjaar', ReportingPeriod: 'Periode',
  GLAccount: 'Grootboekrekening (ID)', GLAccountCode: 'Grootboekrekening', GLAccountDescription: 'Rekeningnaam',
  Account: 'Relatie (ID)', AccountCode: 'Relatiecode', AccountName: 'Relatie',
  Description: 'Omschrijving', Notes: 'Notities', Subject: 'Onderwerp', Body: 'Inhoud',
  AmountDC: 'Bedrag (EUR)', AmountFC: 'Bedrag (valuta)', Amount: 'Bedrag', AmountDebit: 'Debet', AmountCredit: 'Credit',
  AmountVATDC: 'BTW-bedrag', VATAmountDC: 'BTW-bedrag', VATAmountFC: 'BTW-bedrag (valuta)', Currency: 'Valuta', ExchangeRate: 'Koers',
  JournalCode: 'Dagboek', JournalDescription: 'Dagboeknaam', Journal: 'Dagboek',
  Document: 'Document (ID)', DocumentNumber: 'Documentnummer', DocumentSubject: 'Document',
  YourRef: 'Uw referentie', OurRef: 'Onze referentie', InvoiceNumber: 'Factuurnummer', InvoiceID: 'Factuur (ID)',
  CostCenter: 'Kostenplaats', CostCenterCode: 'Kostenplaats', CostCenterDescription: 'Kostenplaatsnaam',
  CostUnit: 'Kostendrager', CostUnitCode: 'Kostendrager', CostUnitDescription: 'Kostendragernaam',
  Type: 'Soort', TypeDescription: 'Soort (tekst)', Status: 'Status', StatusDescription: 'Status (tekst)',
  VATCode: 'BTW-code', VATPercentage: 'BTW-%', VATCodeDescription: 'BTW-omschrijving',
  Supplier: 'Leverancier (ID)', SupplierCode: 'Leverancierscode', SupplierName: 'Leverancier',
  Customer: 'Klant (ID)', CustomerCode: 'Klantcode', CustomerName: 'Klant',
  PaymentCondition: 'Betaalconditie', PaymentConditionDescription: 'Betaalconditie (tekst)', PaymentMethod: 'Betaalwijze',
  PaymentReference: 'Betalingskenmerk', DiscountDueDate: 'Kortingsdatum',
  BalanceType: 'Balans of W&V', Count: 'Aantal boekingen', OffsetID: 'Tegenboeking (ID)',
  Code: 'Code', Name: 'Naam', City: 'Plaats', Postcode: 'Postcode', AddressLine1: 'Adres', AddressLine2: 'Adres 2', AddressLine3: 'Adres 3',
  Country: 'Land', CountryName: 'Land (naam)', State: 'Provincie', StateDescription: 'Provincie (naam)',
  Email: 'E-mail', Phone: 'Telefoon', Website: 'Website', VATNumber: 'BTW-nummer', ChamberOfCommerce: 'KvK-nummer',
  IsSupplier: 'Is leverancier', IsSales: 'Is klant', IsPurchase: 'Koopt in', Blocked: 'Geblokkeerd',
  Classification: 'Classificatie', ClassificationDescription: 'Classificatie (tekst)', BankAccount: 'Bankrekening',
  SearchCode: 'Zoekcode', Language: 'Taal', PaymentConditionPurchase: 'Betaalconditie inkoop', PaymentConditionSales: 'Betaalconditie verkoop',
  Main: 'Hoofdadres', Mailing: 'Postadres', ContactName: 'Contactpersoon',
  InvoiceTo: 'Factuur aan (ID)', InvoiceToName: 'Factuur aan', OrderedBy: 'Besteld door (ID)', OrderedByName: 'Besteld door',
  Item: 'Artikel (ID)', ItemCode: 'Artikelcode', ItemDescription: 'Artikel', Quantity: 'Aantal', UnitCode: 'Eenheid', UnitPrice: 'Prijs per stuk',
  Discount: 'Korting', Project: 'Project',
  Budget: 'Budget', BudgetScenario: 'Budgetscenario', BudgetScenarioCode: 'Budgetscenario',
  FileName: 'Bestand', FileSize: 'Grootte', Url: 'Adres',
}

export function veldLabel(veld: string): string {
  return EIGEN_VELDEN[veld] ?? LABELS[veld] ?? veld
}

/* ------------------------------------------------------------------ *
 *  Wat voor waarde is dit
 * ------------------------------------------------------------------ */

const DATUM = /^\/Date\((-?\d+)(?:[+-]\d+)?\)\/$/
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isGuid = (x: unknown): x is string => typeof x === 'string' && GUID.test(x)

export function exactDatumMs(x: unknown): number | null {
  if (typeof x !== 'string') return null
  const m = DATUM.exec(x)
  return m ? Number(m[1]) : null
}

/** Is dit veld een bedrag? Op de naam, want een bedrag van nul ziet er hetzelfde uit als een aantal. */
export function isBedragVeld(veld: string): boolean {
  if (veld === '@bedrag') return true
  return /^(Amount|VATAmount|AmountVAT)|Amount(DC|FC|Debit|Credit)?$|Price$|^Budget$/.test(veld) && !/Count|Percentage/.test(veld)
}

export function isDatumVeld(veld: string): boolean {
  return veld === '@datum' || /Date$|^Date$|^Created$|^Modified$/.test(veld)
}

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' })
const euroKaal = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const getal = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 6 })
const datumFmt = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
const datumTijdFmt = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })

/* Kleiner dan een halve cent is nul: anders staat er na het optellen van een kolom "€ -0,00". */
const nulDicht = (n: number) => (Math.abs(n) < 0.005 ? 0 : n)

export const bedrag = (n: number | null | undefined) => (n === null || n === undefined || !Number.isFinite(n) ? '' : euro.format(nulDicht(n)))
export const bedragKaal = (n: number | null | undefined) => (n === null || n === undefined || !Number.isFinite(n) ? '' : euroKaal.format(nulDicht(n)))
export const aantal = (n: number | null | undefined) => (n === null || n === undefined ? '' : getal.format(n))

/*
 * Een datum van Exact is middernacht in UTC: /Date(1696118400000)/ is 1 oktober.
 * Als datum tonen we hem dus in UTC (anders wordt 1 oktober in de winter
 * 30 september 23:00 → 30 september). Created en Modified zijn echte
 * tijdstippen; die in onze tijd.
 */
export function datumTekst(ms: number | null | undefined, metTijd = false): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return ''
  return metTijd ? datumTijdFmt.format(new Date(ms)) : datumFmt.format(new Date(ms))
}

/** Een waarde zoals hij in een cel staat. */
export function waardeTekst(veld: string, w: unknown): string {
  if (w === null || w === undefined || w === '') return ''
  if (veld === '@division') return bvNaam(String(w))
  if (veld === '@datum') return datumTekst(typeof w === 'number' ? w : null)
  if (typeof w === 'boolean') return w ? 'ja' : 'nee'
  if (typeof w === 'number') {
    if (isBedragVeld(veld)) return bedragKaal(w)
    return aantal(w)
  }
  if (typeof w === 'string') {
    const ms = exactDatumMs(w)
    if (ms !== null) return datumTekst(ms, veld === 'Created' || veld === 'Modified')
    return w.trim()
  }
  if (typeof w === 'object') return JSON.stringify(w)
  return String(w)
}

/** Wat er in een export komt: getallen als getal, datums als yyyy-mm-dd. */
export function waardeExport(veld: string, w: unknown): string | number | boolean | null {
  if (w === null || w === undefined) return null
  if (veld === '@division') return String(w)
  if (veld === '@datum') return typeof w === 'number' ? new Date(w).toISOString().slice(0, 10) : null
  if (typeof w === 'number' || typeof w === 'boolean') return w
  if (typeof w === 'string') {
    const ms = exactDatumMs(w)
    if (ms !== null) {
      const iso = new Date(ms).toISOString()
      return veld === 'Created' || veld === 'Modified' ? iso.slice(0, 19).replace('T', ' ') : iso.slice(0, 10)
    }
    return w.trim()
  }
  return JSON.stringify(w)
}

/**
 * Een waarde uit een archiefrij, met de eigen velden bovenaan.
 *
 * De vorm is hier uitgeschreven en niet `Record<string, unknown>`: een
 * ArchiefRij uit gedeeld/types.ts heeft geen indexhandtekening en paste
 * daardoor niet, terwijl het juist de rij is waar deze functie voor bestaat.
 */
export function uitRij(
  r: {
    rij?: Record<string, unknown>
    datum?: unknown
    bedrag?: unknown
    nummer?: unknown
    omschrijving?: unknown
    division?: unknown
    exactId?: unknown
  },
  veld: string,
): unknown {
  switch (veld) {
    case '@datum': return r.datum
    case '@bedrag': return r.bedrag
    case '@nummer': return r.nummer
    case '@omschrijving': return r.omschrijving
    case '@division': return r.division
    case '@exactId': return r.exactId
    default: return r.rij?.[veld]
  }
}

/** Is dit een veld waar je op wilt kunnen doorklikken? */
export function soortLink(veld: string): 'boeking' | 'relatie' | 'rekening' | 'document' | null {
  if (veld === 'EntryID') return 'boeking'
  if (veld === 'AccountCode' || veld === 'SupplierCode' || veld === 'CustomerCode') return 'relatie'
  if (veld === 'GLAccountCode') return 'rekening'
  if (veld === 'Document') return 'document'
  return null
}
